'use client';

import { useState } from 'react';
import { styled, Tooltip, Typography } from '@mui/material';
import { TableRow, TableCell, TableActionCell } from '@/ui';
import DateTime from '@components/shared/DateTime';
import ConfirmDialog from '@components/shared/ConfirmDialog';
import useDeleteContact from '@lib/contacts/useDeleteContact';
import toast from 'react-hot-toast';

const SubText = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[600],
  fontSize: '11px',
}));

const iconBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  border: 'none',
  background: 'transparent',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  color: 'var(--action-primary)',
  transition: 'background-color 150ms ease, color 150ms ease',
  padding: 0,
};

function EmailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 7L12 13L2 7" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
    </svg>
  );
}

export default function ContactRow({ contact, openForEdit, openAddNote }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { mutate: deleteContact } = useDeleteContact();

  const handleConfirmDelete = () => {
    setConfirmOpen(false);
    deleteContact(contact.id);
    toast.success('Contact deleted');
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <span style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 13 }}>{contact.first_name} {contact.last_name}</span>
          <SubText>{contact.job_title}</SubText>
        </TableCell>
        <TableCell>{contact.company}</TableCell>
        <TableCell>
          {contact.last_contacted_at && <DateTime date={contact.last_contacted_at} />}
        </TableCell>
        <TableCell>
          <DateTime date={contact.updated_at} />
        </TableCell>
        <TableCell style={{ textAlign: 'right' }}>
          <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Tooltip title={contact.email || 'No email'} placement="top" arrow>
              {contact.email ? (
                <a href={`mailto:${contact.email}`} style={iconBtn}>
                  <EmailIcon />
                </a>
              ) : (
                <span style={{ ...iconBtn, color: 'rgba(17,24,39,0.25)', cursor: 'default' }}>
                  <EmailIcon />
                </span>
              )}
            </Tooltip>
            <Tooltip title={contact.phone || 'No phone'} placement="top" arrow>
              {contact.phone ? (
                <a href={`tel:${contact.phone}`} style={iconBtn}>
                  <PhoneIcon />
                </a>
              ) : (
                <span style={{ ...iconBtn, color: 'rgba(17,24,39,0.25)', cursor: 'default' }}>
                  <PhoneIcon />
                </span>
              )}
            </Tooltip>
          </div>
        </TableCell>
        <TableActionCell items={[
          { label: 'Edit', onClick: () => openForEdit(contact) },
          { label: 'Delete', onClick: () => setConfirmOpen(true), destructive: true },
        ]} />
      </TableRow>
      <ConfirmDialog
        title="Delete contact"
        message={`Are you sure you want to delete ${contact.first_name} ${contact.last_name} from your contacts? This will also delete all notes and interactions with this contact.`}
        open={confirmOpen}
        onConfirm={handleConfirmDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}
